import { axios } from 'common/utils'
import { StoreZzims } from 'common/types/entities/store'
import { ZzimResponse, ZzimProducts } from 'common/types/entities/api/zzim'
import hostConfig from 'common/config'
import { SubVertical, getSubVerticalPath, resolveZzimErrorMessage, ZzimErrorCode } from 'common/constants'
import { BrandAggregate } from 'common/types/entities/mongo/brandAggregate'
import { checkAndGoNaverLogin } from 'common/utils/naverLogin'
import logger from 'common/logger'
import { getHeaders } from 'common/utils/ajax/axios'
import { ArrayUtils } from '@shopping/common'
import { Error } from 'common/types/entities/error'
import HttpStatus from 'common/utils/httpStatus'

export const fetchStoreZzims = async (storeIds: string[], headers?: any): Promise<StoreZzims> => {
  if (!storeIds || !storeIds.length) return null
  const result = await axios.get<StoreZzims>(`/v1/zzims/stores/${storeIds.join(',')}`, getHeaders(headers))
  return result && result.data
}

export const fetchStoreZzimsByVertical = async (subVertical: SubVertical, headers?: any): Promise<string[]> => {
  const result = await axios.get<string[]>(`/v1/zzims${getSubVerticalPath(subVertical)}/stores`, {
    ...getHeaders(headers),
  })

  return result && result.data
}

export const fetchZzimedProducts = async (productIds: string[], headers?: any): Promise<ZzimProducts> => {
  if (ArrayUtils.isEmpty(productIds)) {
    return null
  }
  const result = await axios.get<ZzimProducts>(`/v1/zzims/products/${productIds.join(',')}`, { ...getHeaders(headers) })
  return result && result.data ? result.data : null
}

export const fetchZzimedRelationProducts = async (
  relationProductIds: string[],
  headers?: any,
): Promise<ZzimProducts> => {
  if (!relationProductIds || !relationProductIds.length) return null
  const result = await axios.get<ZzimProducts>(`/v1/zzims/relation-products/${relationProductIds.join(',')}`, {
    ...getHeaders(headers),
  })
  return result && result.data ? result.data : null
}

export const setProductZzim = async (
  productId: string,
  zzimed: boolean,
  extensionType?: string,
  extensionKey?: string,
): Promise<ZzimResponse> => {
  const params = {
    chpid: productId,
  }
  extensionType && (params['extsCd'] = extensionType)
  extensionKey && (params['extsId'] = extensionKey)

  const pcZzimUris = hostConfig.zzimUri.pc
  const url = zzimed ? pcZzimUris.removeProductZzim : pcZzimUris.setProductZzim

  const response = await axios.get<ZzimResponse>(url, {
    params,
    withCredentials: true,
  })
  return response && response.data
}

export const toggleZzimWithNaverLogin = async (productId: string, zzimed: boolean): Promise<boolean> => {
  if (!checkAndGoNaverLogin()) {
    return Promise.resolve(false)
  }

  let response
  try {
    response = await setProductZzim(productId, zzimed)
    if (response.bSuccess) return true

    if (response.sErrorCode === 'ACCESS_DENIED') {
      checkAndGoNaverLogin()
    }

    const error: Error = {
      status: response.sErrorCode === 'ACCESS_DENIED' ? HttpStatus.UNAUTHORIZED : HttpStatus.OK,
      message: resolveZzimErrorMessage(response.htReturnValue.sErrorCode, zzimed, response.htReturnValue.sMessage),
    }
    throw error
  } catch (error) {
    alert(error.message)
    logger.info(error)
    return false
  }
}

export const setRelationProductZzim = async (productId: string, zzimed: boolean): Promise<ZzimResponse> => {
  const params = {
    relationProductId: productId,
  }
  const pcZzimUris = hostConfig.zzimUri.pc
  const url = zzimed ? pcZzimUris.removeRelationProductZzim : pcZzimUris.setRelationProductZzim

  const response = await axios.get<ZzimResponse>(url, {
    params,
    withCredentials: true,
  })
  return response && response.data
}

export const fetchBrandZzims = async (subVertical: SubVertical, headers?: any): Promise<BrandAggregate[]> => {
  const result = await axios.get<BrandAggregate[]>(`/v1/zzims/brand/subVertical/${subVertical}`, {
    ...getHeaders(headers),
  })
  return result && result.data
}

export const fetchRecent100BrandZzimsWithSubVertical = async (headers?: any) => {
  const result = await axios.get<BrandAggregate[]>(`/v1/zzims/brand`, {
    ...getHeaders(headers),
    params: { sort: '-createdAt', limit: 100 },
  })
  const aggregates: BrandAggregate[] = result && result.data

  return aggregates.reduce((subVerticalToIds, brandAgg) => {
    brandAgg.subVerticals.forEach(sub => (subVerticalToIds[sub] = [...(subVerticalToIds[sub] || []), brandAgg._id]))
    return subVerticalToIds
  }, {})
}

export const deleteBrandZzim = async (brandId: string, headers?: any): Promise<void> => {
  await axios.delete(`/v1/zzims/brand/${brandId}`, {
    ...getHeaders(headers),
  })
}

export const fetchZzimedProductsCount = async (headers?: any): Promise<number> => {
  const result = await axios.get<number>('/v1/zzims/products-count', getHeaders(headers))
  return result && result.data ? result.data : 0
}

export const getZzimBrandIds = async (subVertical, queryParams, headers) => {
  if (subVertical !== SubVertical.DEPARTMENT && subVertical !== SubVertical.OUTLET) return null

  if (!queryParams.zzim) return null

  return await fetchRecent100BrandZzimsWithSubVertical(headers).then(result => result[subVertical])
}

export const getZzimStoreIds = async (subVertical, queryParams, headers) => {
  if (subVertical !== SubVertical.STYLE) return null

  if (!queryParams.zzim) return null

  if (!headers || !headers.cookie || !headers.cookie.includes('NID_SES')) return null

  return await fetchStoreZzimsByVertical(subVertical, headers)
}
